import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Edit2 } from 'lucide-react';
import { getPIVisibleTabs, hasPI } from '../../lib/permissions';
import { piApi } from '../../services/piApi';
import PIOverviewTab from './tabs/PIOverviewTab';
import PIVCPTab from './tabs/PIVCPTab';
import PIEBITDABridgeTab from './tabs/PIEBITDABridgeTab';
import PISynergyTab from './tabs/PISynergyTab';
import PIAddonTab from './tabs/PIAddonTab';
import PIReturnsTab from './tabs/PIReturnsTab';
import PIExitReadinessTab from './tabs/PIExitReadinessTab';
import PITalentTab from './tabs/PITalentTab';
import PICashTaxTab from './tabs/PICashTaxTab';
import PILPReportingTab from './tabs/PILPReportingTab';
import PIIntegrationsTab from './tabs/PIIntegrationsTab';
import PIDDReferenceTab from './tabs/PIDDReferenceTab';
import '../pi/PIShell.css';
import './PIPortcoShell.css';

const TAB_CONFIG = [
  { id: 'overview',   label: 'Overview',        path: 'overview' },
  { id: 'vcp',        label: 'Value Creation',  path: 'vcp' },
  { id: 'bridge',     label: 'EBITDA Bridge',   path: 'bridge' },
  { id: 'synergies',  label: 'Synergies',       path: 'synergies' },
  { id: 'addons',     label: 'Add-Ons',         path: 'addons' },
  { id: 'returns',    label: 'Exit Scenarios',  path: 'returns' },
  { id: 'exit',       label: 'Exit Readiness',  path: 'exit' },
  { id: 'talent',     label: 'Leadership',      path: 'talent' },
  { id: 'cash',       label: 'Cash & Tax',      path: 'cash' },
  { id: 'lp',           label: 'LP Reports',    path: 'lp' },
  { id: 'integrations', label: 'Integrations',  path: 'integrations' },
  { id: 'dd',           label: 'DD Reference',  path: 'dd' },
];

function ExitRingLarge({ score }) {
  const color = score >= 70 ? '#16a34a' : score >= 40 ? '#d97706' : '#dc2626';
  const r = 20;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <svg width={48} height={48} viewBox="0 0 48 48" aria-label={`Exit readiness: ${score}/100`}>
        <circle cx={24} cy={24} r={r} fill="none" stroke="#f1f5f9" strokeWidth={4} />
        <circle cx={24} cy={24} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 24 24)" />
        <text x={24} y={28} textAnchor="middle" fontSize={11} fontWeight="700" fill={color}>{score}</text>
      </svg>
      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Exit Readiness</span>
    </div>
  );
}

export default function PIPortcoShell({ currentUser, onPortcoSelect }) {
  const { portcoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [portco, setPortco] = useState(null);
  const [loading, setLoading] = useState(true);

  const visibleTabs = getPIVisibleTabs(currentUser);
  const tabs = TAB_CONFIG.filter(t => visibleTabs.includes(t.id));

  const activeTab = location.pathname.split('/').pop();

  useEffect(() => {
    if (portcoId) {
      if (onPortcoSelect) onPortcoSelect(portcoId);
      piApi.getPortco(portcoId)
        .then(r => setPortco(r.data))
        .catch(e => console.error('[Portco Shell]', e))
        .finally(() => setLoading(false));
    }
  }, [portcoId]);

  if (loading) return (
    <div className="pi-loading" style={{ padding: 40 }}>
      <div className="pi-spinner" />
      <span>Loading portfolio company...</span>
    </div>
  );

  if (!portco) return (
    <div className="pi-empty" style={{ padding: 60 }}>
      <div className="pi-empty-title">Portfolio company not found</div>
      <div className="pi-empty-sub">This company may have been deleted or you may not have access.</div>
      <button className="pi-btn-ghost" onClick={() => navigate('/pi/dashboard')}>Back to Dashboard</button>
    </div>
  );

  const holdMonths = portco.close_date
    ? Math.floor((Date.now() - new Date(portco.close_date).getTime()) / (1000 * 60 * 60 * 24 * 30))
    : null;
  const holdStr = holdMonths != null
    ? holdMonths >= 12 ? `${Math.floor(holdMonths / 12)}y ${holdMonths % 12}m` : `${holdMonths}m`
    : '—';

  return (
    <div className="pi-portco-shell">
      {/* ── Header Bar ────────────────────────────────────────────────── */}
      <div className="pi-portco-header">
        <div className="pi-portco-header-left">
          <h2 className="pi-portco-title">{portco.name}</h2>
          {portco.sector && <span className="pi-badge pi-badge-blue">{portco.sector}</span>}
        </div>
        <div className="pi-portco-header-meta">
          <span className="pi-meta-item">
            <span className="pi-meta-label">Close</span>
            <span className="pi-meta-value">
              {portco.close_date ? new Date(portco.close_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
            </span>
          </span>
          <span className="pi-meta-sep">|</span>
          <span className="pi-meta-item">
            <span className="pi-meta-label">Hold</span>
            <span className="pi-meta-value">{holdStr}</span>
          </span>
          <span className="pi-meta-sep">|</span>
          <span className="pi-meta-item">
            <span className="pi-meta-label">Entry Mult.</span>
            <span className="pi-meta-value">{portco.entry_multiple != null ? `${portco.entry_multiple}x` : '—'}</span>
          </span>
          <span className="pi-meta-sep">|</span>
          <span className="pi-meta-item">
            <span className="pi-meta-label">Entry EBITDA</span>
            <span className="pi-meta-value">{portco.entry_ebitda != null ? `$${portco.entry_ebitda}M` : '—'}</span>
          </span>
        </div>
        <div className="pi-portco-header-right">
          <ExitRingLarge score={portco.exit_readiness_score || 0} />
          {hasPI(currentUser, 'pi.exit.edit') && (
            <button className="pi-btn-ghost" style={{ padding: '5px 8px' }}
              onClick={() => navigate(`/pi/portco/${portcoId}/exit`)}
              aria-label="Edit exit readiness">
              <Edit2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile banner */}
      <div className="pi-mobile-banner">
        DealNexa PI is optimised for desktop. Some features may be limited on smaller screens.
      </div>

      {/* ── Tab Content ───────────────────────────────────────────────── */}
      <div className="pi-portco-content" role="tabpanel">
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<PIOverviewTab portco={portco} currentUser={currentUser} />} />
          <Route path="vcp"      element={<PIVCPTab portco={portco} currentUser={currentUser} />} />
          <Route path="bridge"   element={<PIEBITDABridgeTab portco={portco} currentUser={currentUser} />} />
          <Route path="synergies" element={<PISynergyTab portco={portco} currentUser={currentUser} />} />
          <Route path="addons"   element={<PIAddonTab portco={portco} currentUser={currentUser} />} />
          <Route path="returns"  element={<PIReturnsTab portco={portco} currentUser={currentUser} />} />
          <Route path="exit"     element={<PIExitReadinessTab portco={portco} currentUser={currentUser} />} />
          <Route path="talent"   element={<PITalentTab portco={portco} currentUser={currentUser} />} />
          <Route path="cash"     element={<PICashTaxTab portco={portco} currentUser={currentUser} />} />
          <Route path="lp"           element={<PILPReportingTab portco={portco} currentUser={currentUser} />} />
          <Route path="integrations" element={<PIIntegrationsTab portco={portco} currentUser={currentUser} />} />
          <Route path="dd"           element={<PIDDReferenceTab portco={portco} />} />
          <Route path="*"            element={<Navigate to="overview" replace />} />
        </Routes>
      </div>
    </div>
  );
}
